import { useIntl } from '@/intl'
import { isValidDateFormat, lengthValidationFieldBirthdate, validateAgeBetweenDates } from '@/scopes/checkout/components/helpers/Validations'
import {
  CarListName,
  CarSheetContent,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/SheetViews'
import { carCouldHaveHigherPrice, useCarRentalServiceStore } from '@/scopes/checkout/components/services/CarRentalFilteringService/store'
import { addCar, editCar } from '@/scopes/checkout/domain/actions'
import { createCarEvent } from '@/scopes/checkout/domain/analytics'
import { useService } from '@/scopes/checkout/domain/hooks'
import { type CheckoutCarRentalServiceOption, ServiceType } from '@/scopes/checkout/domain/types'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { space } from '@sunwebgroup/ui-components'
import {
  Banner,
  Button,
  CheckboxField,
  DateField,
  FormField,
  RadioButtonField,
  RadioButtonGroup,
  Sheet,
  Typography,
} from '@sunwebgroup/ui-components/uds'
import { sub } from 'date-fns'
import * as React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { styled } from 'styled-components'

interface DriverFormValues {
  driverId: string
  driverBirthdate: string
  hasCreditCard: boolean
}

const Form = styled.form``

const FormFieldContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_8')};
`
const RadioButtonGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_8')};
`

export function DriverForm({ showDriverForm, setShowDriverForm, ...option }: CheckoutCarRentalServiceOption & { showDriverForm: boolean, setShowDriverForm: (value: boolean) => void }) {
  const { service, setServiceValue } = useService(ServiceType.CarRentalFiltering)
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { endEditingCar, setCarToBeSelected,
  } = useCarRentalServiceStore()
  const { availableDrivers, participants, configurations, carLists } = service || {}
  const { driverId, label, value: carValue, id: carId } = option

  const driverMinBirthdate = sub(new Date(), { years: 99 })
  const driverMaxBirthdate = sub(new Date(), { years: configurations?.minAgeDriver, days: 1 })
  const defaultDriverBirthdate = sub(new Date(), { years: configurations?.rentalConditionsMinAgeDriver, days: 1 })
  const driverMinAgeRentalConditions = sub(new Date(), { years: configurations?.rentalConditionsMinAgeDriver })
  const driverMaxAgeRentalConditions = sub(new Date(), { years: configurations?.rentalConditionsMaxAgeDriver })
  const driver = driverId ? participants?.[driverId] : null
  const [showWarningDriverAge, setShowWarningDriverAge] = React.useState(useCarRentalServiceStore(carCouldHaveHigherPrice(carId, driverMaxAgeRentalConditions, driverMinAgeRentalConditions)))

  const {
    trigger,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<DriverFormValues>({
    mode: 'onBlur',
    defaultValues: {
      driverId: driverId ?? '',
      driverBirthdate: driver?.birthdate ? new Date(driver.birthdate).toISOString().split('T')[0] : new Date(defaultDriverBirthdate).toISOString().split('T')[0],
      hasCreditCard: Boolean(driverId),
    },
  })

  const handleDriverChange = (value: string) => {
    setValue('driverId', value)
    const selectedDriver = participants?.[value]

    setValue(
      'driverBirthdate',
      selectedDriver?.birthdate
        ? new Date(selectedDriver.birthdate).toISOString().split('T')[0]
        : new Date(defaultDriverBirthdate).toISOString().split('T')[0],
    )
    setValue('hasCreditCard', selectedDriver?.id ? (driver?.id === selectedDriver?.id) : false)
  }

  const onSubmit = React.useCallback((result: DriverFormValues) => {
    if (!service)
      return
    setCarToBeSelected('')
    endEditingCar(carId)
    setShowDriverForm(false)
    const updatedBirthdate = new Date(result.driverBirthdate) // Ensure latest value is used
    if (driver) {
      setServiceValue(editCar({
        value: carValue,
        id: carId,
        participantId: result.driverId ?? '',
        driverBirthDate: updatedBirthdate,
        bookedEquipmentIds: [],
      })(service))
      sendEvent(
        createCarEvent({
          result: 'Car edited',
          type: carValue,
          description: 'editCarDriver',
          element: 'button',
          validated: true,
        }),
      )
    }
    else {
      setServiceValue(addCar({
        value: carValue,
        id: carId,
        participantId: result.driverId ?? '',
        driverBirthDate: updatedBirthdate,
        bookedEquipmentIds: [],
      })(service))
      sendEvent(
        createCarEvent({
          result: 'Car added',
          type: carValue,
          description: 'selectCar',
          element: 'button',
          validated: true,
        }),
      )
    }
  }, [driver, setShowDriverForm, service, setServiceValue, endEditingCar, option, sendEvent, setCarToBeSelected])

  if (!service)
    return null
  if (!option)
    return null
  if (!participants)
    return null

  return (
    <>
      <Sheet.Content>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <CarSheetContent>
            <CarListName $variant="display-h-regular">
              {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_car_model' }, { carModel: label })}
              {' '}
            </CarListName>
            <Typography $variant="body-m-regular-snug">{intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_intro_text' })}</Typography>
            <FormField
              label={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_label' })}
              helpText={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_hint' })}
            >
              <FormFieldContent>
                <Controller
                  name="driverId"
                  control={control}
                  rules={{
                    required: intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_required_message' }),
                  }}

                  render={({ field }) => (
                    <RadioButtonGroup defaultValue={field.value} onValueChange={handleDriverChange}>
                      <RadioButtonGroupContainer>
                        {availableDrivers?.map((availableDriver) => {
                          const isDisabled = driverId === availableDriver ? false : Boolean(carLists?.find(car => car.driverId === availableDriver))

                          return (
                            <RadioButtonField key={availableDriver} variant="outlined" disabled={isDisabled} value={availableDriver} label={participants[availableDriver]?.name} />
                          )
                        })}
                      </RadioButtonGroupContainer>
                    </RadioButtonGroup>
                  )}
                />
                {errors.driverId && (
                  <Banner color="danger" hasIcon text={errors.driverId.message ?? ''} size="M" />
                )}
              </FormFieldContent>
            </FormField>

            <FormField
              label={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_birthdate' })}
              feedbackText={errors?.driverBirthdate ? (errors?.driverBirthdate?.message ?? '') : undefined}
              status={errors?.driverBirthdate ? 'error' : undefined}
            >
              <FormFieldContent>
                <Controller
                  name="driverBirthdate"
                  control={control}
                  rules={{
                    validate: {
                      validateAge: (value: unknown) => {
                        if (typeof value !== 'string') {
                          return 'Date not valid'
                        }
                        if (!value) {
                          return true
                        }
                        const isValidAge = validateAgeBetweenDates(value, driverMinBirthdate, driverMaxBirthdate)

                        if (isValidAge) {
                          const driverBirthdateSelectedDate = new Date(value)
                          const isLegalAge = (driverBirthdateSelectedDate >= driverMaxAgeRentalConditions && driverBirthdateSelectedDate <= driverMinAgeRentalConditions)
                          setShowWarningDriverAge(!isLegalAge)
                        }

                        return (
                          isValidAge
                          || intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_birthdate_no_legal_age_message' })
                        )
                      },
                    },
                  }}
                  render={({ field }) => (
                    <DateField
                      {...field}
                      onChange={(e) => {
                        const newValue = e.target.value
                        field.onChange(e)
                        setValue(`driverBirthdate`, newValue, {
                          shouldValidate:
                            Boolean(errors?.driverBirthdate)
                            && isValidDateFormat(newValue)
                            && newValue.length >= lengthValidationFieldBirthdate,
                        })
                      }}
                      onBlur={() => {
                        if (field.value && field.value.length >= lengthValidationFieldBirthdate) {
                          return trigger(`driverBirthdate`)
                        }
                        field.onBlur()
                      }}
                      maxDate={driverMaxBirthdate.toISOString().split('T')[0]}
                      minDate={driverMinBirthdate.toISOString().split('T')[0]}
                      attributes={{ 'data-empty': `${!field.value}` }}
                    />
                  )}
                />

                {!errors?.driverBirthdate && showWarningDriverAge && (
                  <Banner
                    color="neutral"
                    text={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_birthdate_out_of_bounds_message' }, {
                      min: configurations?.rentalConditionsMinAgeDriver,
                      max: configurations?.rentalConditionsMaxAgeDriver,
                    })}
                    size="S"
                  />
                )}
              </FormFieldContent>

            </FormField>

            <FormField
              status={errors.hasCreditCard ? 'error' : undefined}
            >
              <FormFieldContent>
                <Controller
                  name="hasCreditCard"
                  control={control}
                  rules={{
                    required: intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_credit_card_required_message' }),
                  }}
                  render={({ field }) => (
                    <CheckboxField
                      label={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_credit_card_label' })}
                      variant="outlined"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      onClick={field.onChange}
                    />
                  )}
                />
                {errors.hasCreditCard && (
                  <Banner color="danger" hasIcon text={errors.hasCreditCard.message ?? ''} size="M" />
                )}
              </FormFieldContent>
            </FormField>
          </CarSheetContent>
        </Form>
      </Sheet.Content>
      <Sheet.Footer>
        <Button $type="primary" $size="M" $fullWidth onClick={handleSubmit(onSubmit)}>
          {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_save_button_text' })}
        </Button>
      </Sheet.Footer>
    </>
  )
}
